import { getFunnelStage } from "./funnelStage";
import { track } from "./telemetry";

/**
 * Core Web Vitals for the first page, without a dependency: LCP from the
 * `largest-contentful-paint` observer, INP as the longest interaction
 * (`event` entries, 40ms threshold) and CLS from `layout-shift` entries
 * without recent input. Reported once, as `web_vitals`, the first time the
 * page is hidden — the moment the metrics are final. The `stage` says
 * which funnel beat the visitor was on when they left, so a slow LCP can
 * be read against the landing drop-off directly.
 */

let started = false;
let reported = false;
let lcp: number | null = null;
let inp: number | null = null;
let cls = 0;
let sawShift = false;

function observe(type: string, cb: (entries: PerformanceEntryList) => void, opts: PerformanceObserverInit = {}): void {
  try {
    if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return;
    const po = new PerformanceObserver((list) => cb(list.getEntries()));
    po.observe({ type, buffered: true, ...opts } as PerformanceObserverInit);
  } catch {
    // unsupported entry type on this engine
  }
}

export function initWebVitals(): void {
  if (started || typeof window === "undefined" || typeof PerformanceObserver === "undefined") return;
  started = true;
  observe("largest-contentful-paint", (entries) => {
    const last = entries[entries.length - 1];
    if (last) lcp = Math.round(last.startTime);
  });
  observe(
    "event",
    (entries) => {
      for (const e of entries) {
        const d = Math.round(e.duration);
        if (inp === null || d > inp) inp = d;
      }
    },
    { durationThreshold: 40 } as PerformanceObserverInit
  );
  observe("layout-shift", (entries) => {
    for (const e of entries as (PerformanceEntry & { value?: number; hadRecentInput?: boolean })[]) {
      if (e.hadRecentInput) continue;
      sawShift = true;
      cls += e.value ?? 0;
    }
  });
  const report = () => {
    if (reported) return;
    if (document.visibilityState !== "hidden") return;
    reported = true;
    track("web_vitals", {
      lcp_ms: lcp,
      inp_ms: inp,
      cls: sawShift ? Math.round(cls * 1000) / 1000 : null,
      stage: getFunnelStage(),
    });
  };
  document.addEventListener("visibilitychange", report);
  window.addEventListener("pagehide", report);
}

/** Test-only. */
export function _resetWebVitalsForTests(): void {
  started = false;
  reported = false;
  lcp = null;
  inp = null;
  cls = 0;
  sawShift = false;
}
